// Entry point: boot, exchange, connect, play.
//
// The whole flow, and the one thing worth knowing about each step:
//
//   1. Read the launch fragment, then ERASE it — the exchange code is a bearer
//      credential and must not linger in location.hash.
//   2. Restore a cached session if there is one. A WebView reload has already
//      SPENT the one-time code, so this is the ordinary path after an
//      OS-driven reload, not an edge case.
//   3. Exchange the code for a Nakama session, server-to-server behind the RPC.
//   4. Find a match: this trip first, then the global pool, silently.
//   5. Play. Every screen is a projection of server state.
//
// Nothing here is partner-specific. The partner arrives in the launch URL, so
// one deployment serves every host.

import './ui/style.css';
import { applySafeArea, readLaunchParams, SUPPORTED_CONTRACT_VERSION } from './boot/launch_params';
import * as bridge from './bridge/bridge';
import { embedDisabledMessage, embedEnabled, fullGameUrl } from './config';
import { MatchGateway } from './net/gateway';
import { findMatch } from './net/lobby';
import { attemptWithTimeout, runReconnectLoop, SingleFlight } from './net/reconnect';
import {
  cacheSession,
  clearCachedSession,
  exchangePartnerCode,
  nakamaClient,
  restoreSession,
  SessionExchangeError,
} from './net/session';
import { watchVisibility } from './net/visibility';
import { MatchController } from './state/match';
import { App } from './ui/app';
import { el } from './ui/dom';
import type { Session } from '@heroiclabs/nakama-js';

const root = document.getElementById('app');
if (!root) throw new Error('#app missing from index.html');

void boot(root);

async function boot(mount: HTMLElement): Promise<void> {
  const params = readLaunchParams();
  applySafeArea(params);
  document.documentElement.lang = params.lang;

  if (params.v !== SUPPORTED_CONTRACT_VERSION) {
    // A host newer than this bundle is a deploy-order problem, not a
    // player-facing crash.
    console.warn(
      `[boot] host speaks launch contract v${params.v}, this build speaks v${SUPPORTED_CONTRACT_VERSION}`,
    );
  }

  // THE KILL SWITCH, checked before anything else — before the session
  // exchange, before a socket, before a match. Baked in at build time from
  // embed.config.json, so this is not a request that can fail or be blocked:
  // when the switch is off, this build simply will not run a match.
  //
  // `ready` still fires, so the host takes its loader down and the rider reads
  // the message instead of watching a spinner until the host's own timeout.
  if (!embedEnabled) {
    bridge.ready();
    renderUnavailable(mount);
    return;
  }

  renderLoading(mount, 'Connecting you to Nouns Hunt…');

  let session: Session;
  try {
    session = restoreSession(params.partner) ?? (await mintSession(params.partner, params));
  } catch (err) {
    const reason = err instanceof SessionExchangeError ? err.reason : 'network';
    // `ready` fires even here: the host's loader must come down so the rider
    // sees the failure and can act on it, rather than watching a spinner until
    // the host's own 15s timeout gives up.
    bridge.ready();
    bridge.analytics(bridge.ANALYTICS_EVENTS.sessionExchangeFailed, { reason });
    renderFatal(mount, reason);
    return;
  }

  const client = nakamaClient();
  const gateway = new MatchGateway(client);

  const controller = new MatchController({
    tx: gateway,
    userId: () => gateway.userId,
    onMatchEnd: (reward, matchAlias) => {
      const matchId = gateway.currentMatchId ?? matchAlias;
      // Idempotent on matchId, in the bridge and again in the host. A
      // double-posted score is invisible until the weekly board is wrong.
      bridge.reportScore({
        // Already resolved by the controller: the server's value when it sent
        // one (zero included), the cumulative match score only for a server
        // that predates the partner leaderboard.
        score: reward.score ?? 0,
        sessionId: matchId,
        matchId,
        ...(reward.sig ? { sig: reward.sig } : {}),
        ...(reward.issuedAt ? { issuedAt: reward.issuedAt } : {}),
      });
    },
    onAnalytics: (event, props) => {
      // The event names are a closed set on both sides; an unknown one is
      // dropped by the host's allow-list rather than forwarded.
      bridge.analytics(event as never, props);
    },
  });

  const app = new App(mount, controller, {
    controller,
    userId: () => gateway.userId,
    onExit: () => {
      controller.quit();
      clearCachedSession(params.partner);
      void gateway.close();
      app.stop();
      bridge.exit();
    },
    onOpenFullGame: () => bridge.send({ type: 'open_full_game', url: fullGameUrl }),
  });

  wireConnection(gateway, controller, params.partner);
  app.start();
  bridge.ready();

  try {
    await gateway.connect(session);
    bridge.analytics(bridge.ANALYTICS_EVENTS.matchJoinRequested, {
      has_trip: params.trip !== null,
    });
    const found = await findMatch(client, session, { partner: params.partner, trip: params.trip });
    await gateway.joinMatch(found.matchId);
    // Nothing else to do — opCode 2 arrives next and drives every screen from
    // here. The controller acknowledges the join itself.
  } catch (err) {
    console.error('[boot] could not join a match', err);
    bridge.analytics(bridge.ANALYTICS_EVENTS.matchJoinFailed, { reason: 'join_failed' });
    controller.fail('join_failed');
  }
}

async function mintSession(
  partnerSlug: string,
  params: ReturnType<typeof readLaunchParams>,
): Promise<Session> {
  const { session } = await exchangePartnerCode(partnerSlug, params.code, {
    trip: params.trip,
    route: params.route,
  });
  cacheSession(partnerSlug, session);
  return session;
}

/**
 * Wires disconnect recovery and background/foreground resync.
 *
 * Both funnel through ONE SingleFlight, because a flapping network fires the
 * socket's close handler per failure and a foregrounding player can arrive in
 * the middle of a retry loop. Two overlapping loops race each other into a
 * superseded socket — the Flutter client's `_isReconnecting` guard exists for
 * exactly this.
 */
function wireConnection(
  gateway: MatchGateway,
  controller: MatchController,
  partnerSlug: string,
): void {
  const flight = new SingleFlight();

  const recover = (trigger: 'socket' | 'resume'): void => {
    if (!gateway.currentMatchId) return; // nothing to get back to yet
    controller.setReconnecting(true);
    bridge.analytics(bridge.ANALYTICS_EVENTS.reconnectStarted, { trigger });

    void flight
      .run(() =>
        runReconnectLoop({
          attempt: () => attemptWithTimeout(() => gateway.reattach()),
        }),
      )
      .then((ok) => {
        controller.setReconnecting(false);
        bridge.analytics(
          ok
            ? bridge.ANALYTICS_EVENTS.reconnectSucceeded
            : bridge.ANALYTICS_EVENTS.reconnectFailed,
          { trigger },
        );
        if (!ok) {
          // The 28s window is up, so the server's 30s grace is gone too. There
          // is no seat to return to.
          clearCachedSession(partnerSlug);
          controller.fail('lost_connection');
        }
      });
  };

  gateway.listen(
    (event) => {
      // opCode 2 is the AUTHORITATIVE "we are back", and it can arrive from a
      // socket the retry loop has already written off. Releasing the guard
      // here as well is what guarantees the overlay always comes down.
      if (event.kind === 'joinedMatch') flight.release();
      controller.handle(event);
    },
    () => recover('socket'),
  );

  watchVisibility({
    // A backgrounded WebView is SUSPENDED, and the socket's close event may
    // never fire. Returning is treated as a soft disconnect and resynced from
    // server state — never by replaying whatever was typed before, which
    // belongs to a round the server has almost certainly already scored.
    onResume: (hiddenForMs) => {
      console.debug(`[visibility] resuming after ${hiddenForMs}ms hidden`);
      recover('resume');
    },
  });
}

// --- pre-game UI ------------------------------------------------------------
// Only the two states that exist BEFORE the controller owns the screen. Every
// other screen lives in ui/screens.ts.

function renderLoading(mount: HTMLElement, label: string): void {
  mount.replaceChildren(
    el('div', { class: 'center' }, [
      el('div', { class: 'spinner', role: 'status', 'aria-label': 'Loading' }),
      el('p', { class: 'muted' }, [label]),
    ]),
  );
}

/// The kill-switch screen.
///
/// Deliberately calm and final: there is no retry, because nothing the rider
/// does will change the answer until we deploy again. The only action is out.
function renderUnavailable(mount: HTMLElement): void {
  const close = el('button', { class: 'secondary' }, ['Close']);
  close.addEventListener('click', () => bridge.exit());

  mount.replaceChildren(
    el('div', { class: 'center' }, [
      el('p', { class: 'title' }, ['Nouns Hunt is unavailable']),
      el('p', { class: 'muted' }, [
        embedDisabledMessage || 'Nouns Hunt is taking a short break. Check back soon.',
      ]),
      el('div', { class: 'actions' }, [close]),
    ]),
  );
}

function renderFatal(mount: HTMLElement, reason: string): void {
  const copy: Record<string, string> = {
    no_code: 'Open the game from the app to play.',
    // The code is single-use, so reloading with the same one fails
    // identically. Only the host can mint a fresh one, which is why the
    // action is "Close" and not "Retry".
    rejected: 'That sign-in link has expired. Close and open the game again.',
    rate_limited: 'Too many tries just now. Wait a moment and open the game again.',
    // A deployment or configuration fault — not anything the rider did, and
    // not anything another tap will fix. Say so plainly instead of blaming
    // their link or their connection.
    unavailable: "Nouns Hunt isn't available right now. Please try again later.",
    network: "We couldn't reach the game server. Check your connection.",
    malformed: 'The game server sent something we could not read.',
  };

  const close = el('button', { class: 'secondary' }, ['Close']);
  close.addEventListener('click', () => bridge.exit());

  mount.replaceChildren(
    el('div', { class: 'center' }, [
      el('p', { class: 'title error' }, ["Couldn't start"]),
      el('p', { class: 'muted' }, [copy[reason] ?? copy['network']!]),
      el('div', { class: 'actions' }, [close]),
    ]),
  );
}
